import React from 'react';
import { Button, Typography, makeStyles } from '@popmenu/common-ui';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useFormContext } from 'react-form';
// @ts-expect-error -- legacy JS module without TypeScript declarations
import SignUpForm from '~/admin/invites/SignUpForm';
import Grid from '~/shared/Grid';
import { usePopmenuConfig } from '~/utils/withPopmenuConfig';
import { useCreateAccountMutation } from '~/libs/gql/mutations/salesforce/createAccountMutation.generated';
import SelfSignUpStepContainer from '../SelfSignUpStepContainer';
import CongratulationsContent from './CongratulationsContent';
import { useSelfSignUpContext } from '../hooks/useSelfSignUpContext';
import { ProgressStep } from './ProgressStep/ProgressStep';
import type { SelfSignUpForm } from '../types';
import { validateStep } from '../helpers/validateFormFields';
import { pushGtmEvent } from '../helpers/pushGtmEvent';
import { getPlanSteps } from '../PlansStep';

const useStyles = makeStyles(({ spacing }) => ({
  signInLink: {
    marginTop: spacing(3),
  },
  title: {
    marginBottom: spacing(3),
  },
}));

const SignUpFormContent = ({
  onSignUpError,
  onSignUpStart,
  onSignUpSuccess,
}: {
  onSignUpError: () => void,
  onSignUpStart: () => void,
  onSignUpSuccess: () => void,
}) => {
  const classes = useStyles();
  const { facebookOauthDisabled } = usePopmenuConfig();
  const { values } = useFormContext<Record<string, unknown>>();
  const emailValue = (values?.email as string) || '';

  return (
    <Grid item xs={12}>
      <Typography align="center" className={classes.title} variant="h5" weight="medium">
        <FormattedMessage defaultMessage="Let's Create Your Account" />
      </Typography>
      <SignUpForm
        afterSignUp={onSignUpSuccess}
        email={emailValue}
        facebookOauthDisabled={facebookOauthDisabled}
        onSignUpError={onSignUpError}
        onSignUpStart={onSignUpStart}
        showAllFields={false}
        style={{ backgroundColor: '#fff' }}
        variant="outlined"
      />
      <Typography align="center" className={classes.signInLink} variant="body1">
        <FormattedMessage defaultMessage="Already have an account? " />
        <Button component={Link} to="/sign-in" variant="text" size="small">
          <FormattedMessage defaultMessage="Sign-in" />
        </Button>
      </Typography>
    </Grid>
  );
};

const SignUpStep = () => {
  const { values } = useFormContext<SelfSignUpForm>();
  const {
    isSignUpComplete,
    setIsSignUpStarted,
    setIsSignUpComplete,
    setIsSignUpErrored,
  } = useSelfSignUpContext();

  const [createAccount, { loading: isCreatingSalesforceAccount, error: createSalesforceAccountError }] = useCreateAccountMutation();
  const [accountSetupStage, setAccountSetupStage] = React.useState<'signup-form' | 'creating' | 'success'>(
    isSignUpComplete ? 'success' : 'signup-form',
  );
  const [hasLocalError, setHasLocalError] = React.useState(false);

  React.useEffect(() => {
    if (accountSetupStage === 'success') {
      setIsSignUpComplete(true);
    }
  }, [accountSetupStage, setIsSignUpComplete]);

  const triggerCreateAccount = React.useCallback(() => {
    const { address } = values;
    const company = values.restaurantName;
    const phone = values.phone?.inputValue;

    let country = address?.originalSuggestion?.country_code;

    if (!country && ['JE', 'GG', 'IM'].includes(address?.regionCode.toUpperCase() || '')) {
      country = address?.regionCode;
    }

    if (!country) {
      country = address?.country || '';
    }

    country = country?.toUpperCase();

    const stepsToValidate = getPlanSteps(values.productPlan ?? 'starter')
      .steps
      .filter(s => s.name !== 'sign_up_step')
      .map(s => validateStep(s.name, values, values.productPlan));

    const isFormValid = [country, ...stepsToValidate].every(Boolean);

    if (!isFormValid) {
      setHasLocalError(true);
      setIsSignUpErrored(true);
      setAccountSetupStage('creating');
      return;
    }

    const input = {
      city: address?.city || '',
      communicationTime: values.communicationTime as string,
      communicationType: values.communicationType as string,
      company: company || '',
      country: country === 'UK' ? 'GB' : country,
      firstName: values.firstName || '',
      lastName: values.lastName || '',
      locations: Number(values.locations),
      menuUrl: values.menuUrl,
      optInInformationalText: values.optInInformationalText,
      optInPromotionalText: values.optInPromotionalText,
      partnerEmail: values.partnerEmail?.trim() || undefined,
      partnerFirstName: values.partnerFirstName?.trim() || undefined,
      partnerLastName: values.partnerLastName?.trim() || undefined,
      phone: phone || '',
      phoneType: values.phoneType as string,
      postalCode: address?.postalCode || '',
      productPlan: values.productPlan || 'starter',
      siteDesignExamples: [(values.selectedRootSiteTemplate)?.screenshotUrl] as string[],
      source: 'Self Sign-up',
      state: address?.regionCode || address?.region || undefined,
      street: address?.street || '',
      websiteUrl: values.websiteUrl,
    };

    setHasLocalError(false);
    setIsSignUpErrored(false);
    setAccountSetupStage('creating');

    void createAccount({
      variables: {
        input,
      },
    }).then(() => {
      setIsSignUpStarted(true);
    }).catch(() => {
      setHasLocalError(true);
      setIsSignUpErrored(true);
      setAccountSetupStage('creating');
    });
  }, [createAccount, setIsSignUpErrored, setIsSignUpStarted, values, setAccountSetupStage]);

  const onSignUpSuccess = React.useCallback(() => {
    setAccountSetupStage('success');
    pushGtmEvent('ssup_signup_complete');
  }, []);

  switch (accountSetupStage) {
    case 'signup-form':
      return (
        <SelfSignUpStepContainer>
          <Grid container direction="column" justifyContent="center">
            <SignUpFormContent
              onSignUpError={() => {
                setAccountSetupStage('signup-form');
                setHasLocalError(true);
                setIsSignUpErrored(true);
                setIsSignUpStarted(false);
              }}
              onSignUpStart={() => {
                setHasLocalError(false);
                setIsSignUpErrored(false);
                setIsSignUpStarted(true);
              }}
              onSignUpSuccess={triggerCreateAccount}
            />
          </Grid>
        </SelfSignUpStepContainer>
      );
    case 'creating':
      return (
        <ProgressStep
          onRetry={triggerCreateAccount}
          hasError={!isCreatingSalesforceAccount && (hasLocalError || Boolean(createSalesforceAccountError))}
          isRetrying={isCreatingSalesforceAccount}
          onComplete={onSignUpSuccess}
        />
      );
    case 'success':
      return (
        <SelfSignUpStepContainer>
          <CongratulationsContent congratulations={getPlanSteps(values.productPlan ?? 'starter').congratulations} />
        </SelfSignUpStepContainer>
      );
    default:
      return null;
  }
};

export default SignUpStep;
